<script setup lang="ts">
import { useEscapeKey } from '../composables/useEscapeKey';
import type { UIRefType } from '../types';
import BuildForm from './BuildForm.vue';

defineProps<{
  initialRepositoryId?: string;
  initialBranch?: string;
  initialPipeline?: string;
  initialRefType?: UIRefType;
}>();

const emit = defineEmits<{
  submit: [{ repositoryId: string; branch: string; pipeline?: string; refType?: 'branch' | 'tag' | 'pull_request' }];
  close: [];
}>();

function handleBackdropClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    emit('close');
  }
}

useEscapeKey(() => emit('close'));
</script>

<template>
  <div class="modal-backdrop" @click="handleBackdropClick">
    <div class="modal">
      <button class="close-btn" @click="emit('close')">&times;</button>
      <BuildForm
        :initial-repository-id="initialRepositoryId"
        :initial-branch="initialBranch"
        :initial-pipeline="initialPipeline"
        :initial-ref-type="initialRefType"
        @submit="(data) => emit('submit', data)"
        @cancel="emit('close')"
      />
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--color-card-bg);
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  /* Flex column so the BuildForm inside can give its pipeline list
     overflow:auto while the footer buttons stay pinned at the bottom.
     Using overflow:auto on the modal itself would scroll the footer
     off-screen when the pipeline list is long. */
  display: flex;
  flex-direction: column;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  z-index: 1;
}

.close-btn:hover {
  color: var(--color-text-primary);
}

/* Override BuildForm styles for modal context */
.modal :deep(.build-form) {
  border: none;
  border-radius: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* allow the pipeline list inside to shrink + scroll */
}

.modal :deep(.build-form h2) {
  padding-right: 40px; /* Make room for close button */
}

@media (max-width: 540px) {
  .modal {
    margin: 16px;
    max-height: calc(100vh - 32px);
  }
}
</style>
